import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useEvents,
  useUpdateEvent,
  type EventWithRelations,
} from "@/hooks/useEvents";
import type { EventStatus } from "@/types/database";
import {
  EVENT_STATUSES,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_COLORS,
} from "@/utils/constants";
import { KanbanColumn } from "./KanbanColumn";
import { EventCard } from "./EventCard";
import { useToast } from "@/hooks/useToast";

export function KanbanBoard() {
  const { data: events = [], isLoading } = useEvents();
  const updateEvent = useUpdateEvent();
  const { toast } = useToast();
  const [activeEvent, setActiveEvent] = useState<EventWithRelations | null>(
    null,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
  );

  const eventsByStatus = EVENT_STATUSES.reduce(
    (acc, status) => {
      acc[status] = events.filter((e) => e.status === status);
      return acc;
    },
    {} as Record<EventStatus, EventWithRelations[]>,
  );

  const handleDragStart = (event: DragStartEvent) => {
    const eventData = events.find((e) => e.id === event.active.id);
    if (eventData) setActiveEvent(eventData);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveEvent(null);

    if (!over) return;

    const activeEventId = active.id as string;
    const newStatus = over.id as EventStatus;

    const currentEvent = events.find((e) => e.id === activeEventId);
    if (!currentEvent || currentEvent.status === newStatus) return;

    try {
      await updateEvent.mutateAsync({
        id: activeEventId,
        updates: { status: newStatus },
      });
      toast({
        title: "Statut mis à jour",
        description: `${currentEvent.name} → ${EVENT_STATUS_LABELS[newStatus]}`,
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {EVENT_STATUSES.filter((s) => s !== "Abandoned").map((status) => (
          <SortableContext
            key={status}
            id={status}
            items={eventsByStatus[status].map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <KanbanColumn
              status={status}
              label={EVENT_STATUS_LABELS[status]}
              color={EVENT_STATUS_COLORS[status]}
              events={eventsByStatus[status]}
            />
          </SortableContext>
        ))}
      </div>
      <DragOverlay>
        {activeEvent ? <EventCard event={activeEvent} isDragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}
